<template>
    <nav
        class="fixed h-screen bg-primary-700 flex flex-col z-50 w-[--sidebar-size-collapsed] hover:w-[300px] group/navbar text-primary-300 text-md font-medium"
        @mouseover="isOpen = true"
        @mouseleave="isOpen = false"
    >
        <!-- LOGO -->
        <div class="bg-white">
            <img
                class="h-[78px] p-[23px] box-border hidden group-hover/navbar:inline"
                src="/src/assets/icons/logo.svg"
            />
            <img
                class="h-[78px] p-[23px] box-border group-hover/navbar:hidden"
                src="/src/assets/icons/logo_collapsed.svg"
            />
        </div>

        <div class="h-full flex flex-col pt-2.5 pb-4 px-4">
            <!-- MAIN NAVIGATION -->
            <ul
                class="flex flex-col justify-between gap-2"
                v-if="currentUser?.is_active"
            >
                <NavItem :to="{ name: 'documents-list' }">
                    <template #icon>
                        <PhFile />
                    </template>
                    <template #label> Documents </template>
                </NavItem>

                <NavItem
                    :to="{ name: 'team' }"
                    v-if="isAdmin"
                >
                    <template #icon>
                        <PhUsersThree />
                    </template>
                    <template #label> Team </template>
                </NavItem>

                <NavItem
                    :to="{ name: 'billing' }"
                    v-if="isAdmin"
                >
                    <template #icon>
                        <PhCurrencyDollar />
                    </template>
                    <template #label> Billing </template>
                </NavItem>

                <!-- Settings sub-menu -->
                <li
                    class="p-3 rounded-md transition-colors duration-200"
                    :class="
                        isSettingsLinkActive
                            ? 'bg-primary-800'
                            : 'hover:bg-primary-800 group/settings'
                    "
                >
                    <div class="flex flex-row gap-4">
                        <PhGear />
                        <div
                            class="hidden group-hover/navbar:flex flex-grow justify-between"
                        >
                            <span> Settings </span>
                            <PhCaretDown
                                class="group-hover/settings:-rotate-180 transition-all duration-200 ease-in-out origin-center"
                            />
                        </div>
                    </div>
                    <ul
                        class="text-sm hidden flex-col items-start gap-4 mt-4 ml-10 [&>li:hover]:text-primary-400"
                        :class="
                            isSettingsLinkActive
                                ? 'group-hover/navbar:flex'
                                : 'group-hover/settings:!flex'
                        "
                    >
                        <li>
                            <RouterLink
                                :to="{ name: 'settings' }"
                                active-class="text-primary-400"
                                v-if="isSuperAdmin"
                            >
                                Organization settings
                            </RouterLink>
                        </li>
                        <li>
                            <RouterLink
                                :to="{ name: 'document-types' }"
                                active-class="text-primary-400"
                            >
                                Structured data fields
                            </RouterLink>
                        </li>
                    </ul>
                </li>

                <!-- Help Wizard sub-menu -->
                <li
                    class="p-3 rounded-md transition-colors duration-200 hover:bg-primary-800 group/help"
                >
                    <div class="flex flex-row gap-4">
                        <PhQuestion />
                        <div
                            class="hidden group-hover/navbar:flex flex-grow justify-between"
                        >
                            <span> Help </span>
                            <PhCaretDown
                                weight="bold"
                                class="group-hover/help:-rotate-180 transition-all duration-200 ease-in-out origin-center"
                            />
                        </div>
                    </div>
                    <ul
                        class="text-sm hidden flex-col items-start gap-4 mt-4 ml-10 [&>li:hover]:text-primary-400 group-hover/help:flex"
                    >
                        <li>
                            <a
                                :href="HELP_URL_LOGIN"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                How to log in
                            </a>
                        </li>
                        <li v-if="isAdmin">
                            <a
                                :href="HELP_URL_ADD_TEAM_MEMBER"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                How to add new team members
                            </a>
                        </li>
                        <li v-if="isAdmin">
                            <a
                                :href="HELP_URL_BILLING"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                How to manage your subscription
                            </a>
                        </li>
                        <li>
                            <a
                                :href="HELP_URL_UPLOAD_DOCUMENT"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                How to upload documents and download extractions
                            </a>
                        </li>
                        <li>
                            <a
                                :href="HELP_URL_EDIT_DOCUMENT_EXTRACTIONS"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                How to review and edit extractions
                            </a>
                        </li>
                    </ul>
                </li>
            </ul>

            <!-- USER AND ORGANIZATION DETAILS -->
            <template v-if="isRegistrationComplete">
                <hr class="border-primary-600 mt-auto" />

                <hr class="border-primary-600" />
                <ul>
                    <NavItem class="group/org">
                        <template #icon>
                            <PhFolder
                                :class="{
                                    'group-hover/org:hidden': isSuperAdmin,
                                }"
                            />
                            <div
                                data-tooltip="Create new organization"
                                data-tooltip-position="right"
                                class="!hidden group-hover/org:!flex cursor-pointer"
                                v-if="isSuperAdmin"
                            >
                                <PhFolderPlus
                                    @click="showNewOrganizationModal = true"
                                />
                            </div>
                        </template>
                        <template #label>
                            <OrganizationList
                                class="hidden group-hover/navbar:flex max-w-48 -my-1"
                            />
                        </template>
                    </NavItem>

                    <NavItem>
                        <template #icon>
                            <PhCoinVertical />
                        </template>
                        <template #label>
                            <div class="flex flex-row justify-between">
                                <span>
                                    {{ creditsRemaining || 0 }} credits
                                </span>
                                <EaiBadge
                                    :colour="
                                        currentOrganization.has_free_trial_expired
                                            ? 'error'
                                            : 'success'
                                    "
                                    v-if="
                                        currentOrganization?.subscription_status ==
                                        'trialing'
                                    "
                                    :data-tooltip="
                                        currentOrganization.has_free_trial_expired
                                            ? 'Free trial expired'
                                            : currentOrganization?.free_trial_end
                                              ? `Free trial ends ${getRelativeTimeString(currentOrganization.free_trial_end)}`
                                              : ''
                                    "
                                >
                                    FREE TRIAL
                                </EaiBadge>
                            </div>
                        </template>
                    </NavItem>

                    <NavItem>
                        <template #icon>
                            <PhUser />
                        </template>
                        <template #label>
                            <span
                                class="flex flex-row justify-between bg-error-200w"
                            >
                                <span>
                                    {{
                                        currentUser?.first_name ||
                                        currentUser?.email
                                    }}
                                </span>
                                <EaiBadge
                                    v-if="userRole"
                                    colour="primary"
                                    class="ml-auto"
                                >
                                    {{ userRole }}
                                </EaiBadge>
                            </span>
                        </template>
                    </NavItem>
                </ul>

                <EaiButton
                    class="ml-auto mt-2 invisible group-hover/navbar:visible"
                    size="small"
                    type="secondary"
                    @click="emit('logout')"
                >
                    Logout
                    <PhSignOut />
                </EaiButton>
            </template>
        </div>

        <EaiModal v-if="isSuperAdmin && showNewOrganizationModal">
            <template #header>Create new organization</template>
            <template #body>
                <OrganizationCreation
                    @organization-created="
                        () => {
                            showNewOrganizationModal = false
                            authStore.retrieveUser()
                        }
                    "
                />
            </template>
            <template #actions>
                <EaiButton
                    type="secondary"
                    class="w-full"
                    @click="showNewOrganizationModal = false"
                >
                    Cancel
                </EaiButton>
            </template>
        </EaiModal>
    </nav>
</template>

<script setup lang="ts">
import { computed, provide, ref } from "vue"
import { storeToRefs } from "pinia"
import { useAuthStore } from "/src/stores/auth"
import EaiButton from "/src/components/core/EaiButton.vue"
import EaiBadge from "/src/components/core/EaiBadge.vue"
import NavItem from "./NavItem.vue"
import { useRoute } from "vue-router"
import OrganizationList from "/src/components/organization/OrganizationList.vue"
import OrganizationCreation from "/src/components/organization/OrganizationCreation.vue"
import EaiModal from "../EaiModal.vue"
import {
    HELP_URL_LOGIN,
    HELP_URL_ADD_TEAM_MEMBER,
    HELP_URL_BILLING,
    HELP_URL_UPLOAD_DOCUMENT,
    HELP_URL_EDIT_DOCUMENT_EXTRACTIONS,
} from "/src/utils/wizards"
import {
    PhCaretDown,
    PhCoinVertical,
    PhCurrencyDollar,
    PhFile,
    PhFolder,
    PhFolderPlus,
    PhGear,
    PhQuestion,
    PhSignOut,
    PhUser,
    PhUsersThree,
} from "@phosphor-icons/vue"
import { useCreditsStore } from "/src/stores/credits"
import { getRelativeTimeString } from "/src/utils/time"

const emit = defineEmits<{ (e: "logout"): void }>()

const authStore = useAuthStore()
const {
    currentUser,
    currentUserRole,
    currentOrganization,
    isRegistrationComplete,
    isSuperAdmin,
    isAdmin,
} = storeToRefs(authStore)

const userRole = computed(() => {
    if (currentOrganization.value) {
        if (currentUserRole.value) return currentUserRole.value
        else if (isSuperAdmin.value) return "superadmin"
    }
    return undefined
})

const route = useRoute()
const isSettingsLinkActive = computed(() => {
    return route.meta?.parentNavbarSection === "settings"
})

// Note, this is only necessary because of the organization selection dropup
const isOpen = ref(false)

const showNewOrganizationModal = ref(false)

// Credits management
const creditsStore = useCreditsStore()
const { creditsRemaining } = storeToRefs(creditsStore)

// Icons configuration
provide("size", "24px")
provide("weight", "bold")
</script>
